import { useNavigate } from "react-router-dom";
import { products, Product } from "@/mocks/products";
import type { Article } from "@/mocks/articles";

interface Props {
  article: Article;
}

// Map article tag → product category
const tagToCategoryMap: Record<string, "dzi" | "crystal" | "jade" | "thangka"> = {
  "天珠文化": "dzi",
  "水晶指南": "crystal",
  "玉石文化": "jade",
  "藝術文化": "thangka",
};

const categoryMeta: Record<string, { label: string; color: string; bg: string; accent: string; icon: string; desc: string }> = {
  dzi: {
    label: "西藏天珠",
    color: "#8b6f47",
    bg: "#f5ede0",
    accent: "#fdf8f2",
    icon: "ri-ancient-gate-line",
    desc: "千年傳承的守護之珠",
  },
  crystal: {
    label: "天然水晶礦石",
    color: "#7a6a9a",
    bg: "#f0edf7",
    accent: "#f8f6fc",
    icon: "ri-sparkling-2-line",
    desc: "大自然孕育的療癒能量",
  },
  jade: {
    label: "翡翠玉石",
    color: "#4a7a52",
    bg: "#eaf2eb",
    accent: "#f4f9f5",
    icon: "ri-leaf-line",
    desc: "東方文化的吉祥象徵",
  },
  thangka: {
    label: "唐卡佛牌",
    color: "#c9a96e",
    bg: "#faf5ec",
    accent: "#fdf9f2",
    icon: "ri-shield-star-line",
    desc: "信仰與藝術的守護力量",
  },
};

export default function RelatedProducts({ article }: Props) {
  const navigate = useNavigate();
  const category = tagToCategoryMap[article.tag];
  const meta = category ? categoryMeta[category] : null;

  // Pick 3 products from the matched category
  const relatedProducts: Product[] = category
    ? products.filter((p) => p.category === category).slice(0, 3)
    : [];

  if (!meta || relatedProducts.length === 0) return null;

  const handleViewAll = () => {
    navigate(`/products?category=${category}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleProduct = () => {
    navigate(`/products?category=${category}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div
      className="rounded-2xl overflow-hidden"
      style={{ background: meta.accent, border: `1px solid ${meta.bg}` }}
    >
      {/* Header */}
      <div
        className="px-5 py-4 flex items-center gap-3"
        style={{ background: meta.bg }}
      >
        <div
          className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0"
          style={{ background: meta.color + "20" }}
        >
          <i className={`${meta.icon} text-base`} style={{ color: meta.color }}></i>
        </div>
        <div>
          <p
            className="text-xs font-semibold leading-tight"
            style={{ color: meta.color, fontFamily: "'Noto Serif TC', serif" }}
          >
            相關商品推薦
          </p>
          <p className="text-[10px] mt-0.5" style={{ color: meta.color + "99" }}>
            {meta.label} · {meta.desc}
          </p>
        </div>
      </div>

      {/* Product List */}
      <div className="p-4 space-y-3">
        {relatedProducts.map((product) => (
          <button
            key={product.id}
            onClick={handleProduct}
            className="w-full flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition-all duration-200 hover:bg-white/70 group text-left"
          >
            {/* Image */}
            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
              <img
                src={product.img}
                alt={product.name}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
              />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              {product.badge && (
                <span
                  className="inline-block text-[9px] font-medium px-1.5 py-0.5 rounded-full mb-1"
                  style={{ background: meta.bg, color: meta.color }}
                >
                  {product.badge}
                </span>
              )}
              <p
                className="text-xs font-semibold text-[#3d3530] leading-tight truncate"
                style={{ fontFamily: "'Noto Serif TC', serif" }}
              >
                {product.name}
              </p>
              <p className="text-[10px] text-[#a89880] mt-0.5 truncate">{product.desc}</p>
              <p
                className="text-xs font-bold mt-1"
                style={{ color: meta.color }}
              >
                NT$ {product.price.toLocaleString()}
              </p>
            </div>

            {/* Arrow */}
            <div
              className="w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              style={{ background: meta.bg }}
            >
              <i className="ri-arrow-right-s-line text-xs" style={{ color: meta.color }}></i>
            </div>
          </button>
        ))}
      </div>

      {/* Footer CTA */}
      <div className="px-4 pb-4">
        <button
          onClick={handleViewAll}
          className="w-full py-2.5 rounded-xl text-xs font-semibold cursor-pointer transition-all duration-200 whitespace-nowrap"
          style={{
            background: meta.color,
            color: "#fff",
            fontFamily: "'Noto Serif TC', serif",
          }}
        >
          查看全部{meta.label}
          <i className="ri-arrow-right-line ml-1 text-xs"></i>
        </button>
      </div>
    </div>
  );
}
